/**
 * Cruise Command · keyboard-shortcut safety helpers.
 * ---------------------------------------------------------------------
 * Any global `window`/`document` keyboard listener must ask this
 * helper "is the user typing right now?" before running its action.
 *
 * P0 fix (Feb 2026) · Shift+D was being intercepted globally to
 * toggle Developer Preview Mode, making it impossible to type a
 * capital "D" in any input. `isEditableFocus` returns true whenever
 * the event's target (or the current document `activeElement`) is a
 * text-entry surface, and shortcut handlers must return immediately.
 */

const EDITABLE_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

/**
 * Is the user actively typing into an editable control?
 * Covers standard form fields, contentEditable regions, ARIA
 * text-boxes and search inputs.
 */
export function isEditableFocus(eventOrElement) {
  const target =
    eventOrElement && eventOrElement.target
      ? eventOrElement.target
      : (eventOrElement || (typeof document !== "undefined" && document.activeElement));
  if (!target || target.nodeType !== 1) return false;
  const tag = (target.tagName || "").toUpperCase();
  if (EDITABLE_TAGS.has(tag)) return true;
  if (target.isContentEditable) return true;
  const role = (target.getAttribute && target.getAttribute("role")) || "";
  if (role === "textbox" || role === "searchbox" || role === "combobox") return true;
  // Guard against nested editors — walk up a few ancestors.
  let node = target.parentElement;
  for (let i = 0; node && i < 4; i += 1) {
    if (node.isContentEditable) return true;
    node = node.parentElement;
  }
  return false;
}

/**
 * Wrap a shortcut handler so it becomes a no-op while the user is
 * typing into an editable surface.
 */
export function guardShortcut(handler) {
  return (event) => {
    if (isEditableFocus(event)) return;
    return handler(event);
  };
}
